import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Play, CalendarDays, Clock, Radio } from 'lucide-react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import { Image } from '@/components/ui/image';
import { useI18n } from '@/lib/i18n';
import { useSeo } from '@/lib/seo';
import {
  findOnAirShow, findUpcomingShow, formatTime, formatDays, getETNow, WEEKDAY_ORDER, WEEKDAY_LABELS,
} from '@/lib/schedule';
import OnAirCard from '@/components/yakaleo/OnAirCard';
import OnAirPill from '@/components/yakaleo/OnAirPill';
import AddToCalendar from '@/components/yakaleo/AddToCalendar';
import Tracklist from '@/components/yakaleo/Tracklist';
import { blurSrc } from '@/lib/artwork';

const DAY_SHORT = {
  es: { monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié', thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom' },
  en: { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' },
};

function Equalizer() {
  return (
    <span className="inline-flex items-end gap-[3px] h-5" aria-hidden="true">
      <style>{`@keyframes ykEq{0%,100%{height:25%}50%{height:100%}}`}</style>
      {[0, 0.25, 0.5, 0.15].map((d, i) => (
        <span key={i} className="w-[3px] rounded-full bg-fuchsia-400" style={{ height: '60%', animation: `ykEq 0.9s ease-in-out ${d}s infinite` }} />
      ))}
    </span>
  );
}

function ShowCard({ show, host, live, upnext, es, lang, t, showUrl, onListen }) {
  return (
    <Link
      to={showUrl(show)}
      className={`group flex gap-4 p-3 md:p-4 bg-white rounded-2xl shadow-sm border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${live ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent hover:border-purple-200'}`}
    >
      <div className="relative flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-gradient-to-br from-purple-700 to-purple-950 shadow">
        {show.artwork_url ? (
          <Image src={show.artwork_url} alt={show.name} className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105" fittingType="fill" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Radio size={24} className="text-white/40" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          {live && <OnAirPill es={es} size="sm" />}
          {upnext && (
            <span className="inline-flex items-center bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase">
              {es ? 'A continuación' : 'Up next'}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-purple-700 text-xs font-black tabular-nums">
            <Clock size={12} /> {formatTime(show.start_time)} – {formatTime(show.end_time)}
          </span>
        </div>
        <p className="text-black text-base md:text-lg font-black leading-tight mt-1 truncate group-hover:text-purple-700 transition-colors">{show.name}</p>
        {host && (
          <div className="flex items-center gap-2 mt-1 min-w-0">
            {host.photo_url && <img src={host.photo_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" loading="lazy" />}
            <p className="text-neutral-600 text-sm truncate">{t('Hosted by')} <span className="font-bold text-black">{host.name}</span></p>
          </div>
        )}
        <p className="text-neutral-400 text-xs mt-1 truncate">{formatDays(show.days, lang)}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-auto pt-2">
          <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="whitespace-nowrap">
            <AddToCalendar show={show} hostName={host?.name} />
          </div>
          {live && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onListen(); }}
              className="inline-flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1 rounded-full text-[11px] font-bold tracking-widest whitespace-nowrap hover:bg-purple-700 transition-colors"
            >
              <Play size={11} fill="currentColor" />
              {es ? 'ESCUCHAR' : 'LISTEN'}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Schedule() {
  const { t, lang } = useI18n();
  const subtitle = t('Your weekly lineup — find out when your favorite shows air.');
  const schedLocation = useLocation();
  const schedEs = !schedLocation.pathname.startsWith('/en/');
  const es = lang === 'es';
  const { liveData, setIsPlaying } = useOutletContext();
  useSeo({
    canonicalPath: schedEs ? '/programacion' : '/en/schedule',
    lang: schedEs ? 'es' : 'en',
    twinPath: schedEs ? '/en/schedule' : '/programacion',
    title: schedEs ? 'Programación | Yakaleo' : 'Schedule | Yakaleo',
    description: schedEs
      ? 'Tu programación semanal — descubre cuándo salen al aire tus programas favoritos.'
      : 'Your weekly lineup — find out when your favorite shows air.',
  });
  const [shows, setShows] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    Promise.all([
      base44.entities.Show.list('sort_order', 100).catch(() => []),
      base44.entities.Host.list('sort_order', 100).catch(() => []),
    ]).then(([s, h]) => {
      setShows(s || []);
      setHosts(h || []);
    }).finally(() => setLoading(false));
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const hostOf = (id) => hosts.find((h) => h.id === id);
  const showUrl = (s) =>
    s?.slug ? (lang === 'es' ? `/programa/${s.slug}` : `/en/show/${s.slug}`) : '/schedule';

  const onAir = findOnAirShow(shows, liveData);
  const onAirHost = onAir?.host_id ? hostOf(onAir.host_id) : null;
  const upcomingRaw = onAir ? null : findUpcomingShow(shows);
  const upcoming = upcomingRaw
    ? { ...upcomingRaw, host: upcomingRaw.show.host_id ? hostOf(upcomingRaw.show.host_id) : null }
    : null;

  const byDay = WEEKDAY_ORDER.map((d) => ({
    day: d,
    label: WEEKDAY_LABELS[d],
    shows: shows
      .filter((s) => s.days?.includes(d))
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')),
  }));

  const etNow = getETNow();
  const todayWeekday = etNow.weekday;
  const nextShowToday = (shows || [])
    .filter((s) => s.days?.includes(todayWeekday))
    .filter((s) => {
      const [sh, sm] = (s.start_time || '00:00').split(':').map(Number);
      return sh * 60 + sm > etNow.minutes;
    })
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))[0];
  const nextShowId = nextShowToday?.id || null;

  const selectedDay = day || todayWeekday;
  const weekView = selectedDay === 'week';
  const selected = byDay.find((d) => d.day === selectedDay) || byDay[0];
  const renderCards = (list) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {list.map((s) => (
        <ShowCard
          key={s.id}
          show={s}
          host={s.host_id ? hostOf(s.host_id) : null}
          live={!!onAir && onAir.id === s.id}
          upnext={!(onAir && onAir.id === s.id) && nextShowId === s.id}
          es={es}
          lang={lang}
          t={t}
          showUrl={showUrl}
          onListen={() => setIsPlaying(true)}
        />
      ))}
    </div>
  );
  const np = liveData?.now_playing;
  const heroArt = onAir?.artwork_url || upcoming?.show?.artwork_url || np?.artwork_url;
  const weeklyShows = shows.filter((s) => s.days?.length).length;

  return (
    <div className="bg-neutral-100 min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden bg-black">
        {heroArt && (
          <img src={blurSrc(heroArt)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-2xl scale-125" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-purple-950/60 to-black/90" />
        <div className="relative max-w-[1100px] mx-auto px-4 md:px-8 py-8 md:py-12 md:flex md:items-center md:justify-between md:gap-10">
          <div className="md:flex-1 md:min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays size={14} className="text-fuchsia-300" />
              <span className="text-white/70 text-xs font-bold tracking-[0.25em]">
                {es ? 'HORA DEL ESTE (ET)' : 'EASTERN TIME (ET)'}{weeklyShows > 0 ? ` · ${weeklyShows} ${es ? 'PROGRAMAS' : 'SHOWS'}` : ''}
              </span>
            </div>
            <h1 className="text-white text-3xl md:text-5xl font-black tracking-tight">{t('STATION SCHEDULE')}</h1>
            <p className="text-white/60 text-sm md:text-base mt-2 max-w-xl">{subtitle}</p>
          </div>

          <div className="mt-6 md:mt-0 md:w-[440px] md:flex-shrink-0 flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-3 md:p-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-neutral-800 flex-shrink-0 shadow-lg">
              {np?.artwork_url && <img src={np.artwork_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-fuchsia-300 text-[11px] font-bold tracking-[0.2em]">
                <Equalizer /> {es ? 'SONANDO AHORA' : 'NOW PLAYING'}
              </p>
              <p className="text-white font-black text-base md:text-lg truncate mt-1">{np?.title || 'Yakaleo Live Radio'}</p>
              <p className="text-white/60 text-sm truncate">{onAir ? onAir.name : np?.artist || 'Reggaetón 24/7'}</p>
            </div>
            <button
              onClick={() => setIsPlaying(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs tracking-widest rounded-full px-4 py-3 transition-colors"
            >
              <Play size={14} fill="currentColor" /> <span className="hidden sm:inline">{es ? 'ESCUCHAR' : 'LISTEN'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Live show, or one starting within 2 hours */}
        {(onAir || upcoming) && (
          <>
            <OnAirCard
              onAir={onAir}
              host={onAirHost}
              liveData={liveData}
              onListen={() => setIsPlaying(true)}
              upcoming={upcoming}
            />
            {onAir && <div className="-mt-2 mb-8"><Tracklist show={onAir} isLive /></div>}
          </>
        )}

        {/* Day picker */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-1 mb-6" role="tablist">
          {byDay.map((d) => {
            const active = d.day === selected.day;
            const today = d.day === todayWeekday;
            return (
              <button
                key={d.day}
                role="tab"
                aria-selected={active}
                onClick={() => setDay(d.day)}
                className={`flex-shrink-0 min-w-[72px] h-14 px-3 rounded-2xl flex flex-col items-center justify-center transition-colors ${active ? 'bg-black text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'}`}
              >
                <span className="text-sm font-black">{DAY_SHORT[es ? 'es' : 'en'][d.day]}</span>
                <span className={`text-[10px] font-bold tracking-widest uppercase ${active ? 'text-fuchsia-300' : today ? 'text-purple-700' : 'text-neutral-400'}`}>
                  {today ? (es ? 'Hoy' : 'Today') : d.shows.length ? `${d.shows.length} show${d.shows.length === 1 ? '' : 's'}` : '—'}
                </span>
              </button>
            );
          })}
          <button
            role="tab"
            aria-selected={weekView}
            onClick={() => setDay('week')}
            className={`flex-shrink-0 min-w-[72px] h-14 px-4 rounded-2xl flex flex-col items-center justify-center transition-colors ${weekView ? 'bg-purple-700 text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'}`}
          >
            <span className="text-sm font-black">{es ? 'Semana' : 'Week'}</span>
            <span className={`text-[10px] font-bold tracking-widest uppercase ${weekView ? 'text-fuchsia-200' : 'text-neutral-400'}`}>{es ? 'Todo' : 'All'}</span>
          </button>
        </div>

        {!weekView && (
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-black tracking-[0.2em] text-purple-700 uppercase">{t(selected.label)}</span>
            {selected.day === todayWeekday && <span className="text-xs text-neutral-400">{es ? 'hoy' : 'today'}</span>}
            <span className="flex-1 h-px bg-gradient-to-r from-neutral-300 to-transparent" />
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-neutral-200 animate-pulse" />)}
          </div>
        ) : weekView ? (
          <div className="space-y-8">
            {byDay.map((d) => (
              <section key={d.day}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-black tracking-[0.2em] text-purple-700 uppercase">{t(d.label)}</span>
                  {d.day === todayWeekday && <span className="text-xs text-neutral-400">{es ? 'hoy' : 'today'}</span>}
                  <span className="flex-1 h-px bg-gradient-to-r from-neutral-300 to-transparent" />
                </div>
                {d.shows.length ? renderCards(d.shows) : (
                  <p className="text-neutral-500 text-sm">{t('Normal Programming')} · Reggaetón 24/7</p>
                )}
              </section>
            ))}
          </div>
        ) : selected.shows.length === 0 ? (
          <div className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-700 to-purple-950 flex items-center justify-center flex-shrink-0">
              <Radio size={22} className="text-white/70" />
            </div>
            <div>
              <p className="text-black font-black">{t('Normal Programming')}</p>
              <p className="text-neutral-500 text-sm">{es ? 'Reggaetón 24/7 todo el día.' : 'Reggaetón 24/7 all day.'}</p>
            </div>
          </div>
        ) : (
          renderCards(selected.shows)
        )}

        <p className="text-neutral-500 text-xs text-center mt-8">{t('All times shown in America/New_York. Stream live 24/7 — the schedule highlights hosted shows and special segments.')}</p>
      </div>
    </div>
  );
}